/** Partner-signup-only session + Stripe return helpers (CSF /partner-signup flow). */

export const PARTNER_SIGNUP_FLOW_PATH = 'partner-signup';

export const PARTNER_SIGNUP_KEYS = {
  awaitingPayment: 'partner_signup_awaiting_payment_return',
  flowPath: 'partner_signup_flow_path',
  authTokenBackup: 'partner_signup_auth_token_backup',
  userBackup: 'partner_signup_user_backup',
  referralCode: 'partner_signup_referral_code',
  referralPromo: 'partner_signup_referral_promo',
  promocode: 'partner_signup_promocode',
} as const;

/** Same keys used by /signup/referral Stripe return (mirrored to localStorage for fresh-browser tabs). */
export const SIGNUP_STRIPE_RETURN_KEYS = {
  awaitingPayment: 'signup_awaiting_payment_return',
  flowPath: 'signup_flow_path',
} as const;

/** True on /signup or /partner-signup (hash-routing aware). */
export function isSignupLandingRoute(url: string = ''): boolean {
  let path = url;
  if (!path && typeof window !== 'undefined') {
    path = `${window.location.pathname || ''}${window.location.hash || ''}`;
  }
  const lower = path.toLowerCase();
  return lower.includes('/partner-signup') || lower.includes('/signup') || lower.includes('/advisor-signup');
}

/** Same keys mirrored in localStorage — survives Stripe redirect when sessionStorage is cleared. */
const PARTNER_STRIPE_RETURN_KEYS = [
  PARTNER_SIGNUP_KEYS.awaitingPayment,
  PARTNER_SIGNUP_KEYS.flowPath,
  PARTNER_SIGNUP_KEYS.authTokenBackup,
  PARTNER_SIGNUP_KEYS.userBackup,
  PARTNER_SIGNUP_KEYS.referralCode,
  PARTNER_SIGNUP_KEYS.referralPromo,
  PARTNER_SIGNUP_KEYS.promocode,
] as const;

const LOCAL_PRESERVE = [
  'token',
  'user',
  'userProfile',
  'promoCodeDetails',
  'isUserTimezoneSet',
  'firstTimeUser',
  'accountType',
  ...PARTNER_STRIPE_RETURN_KEYS,
] as const;

const SESSION_PRESERVE = [
  'user',
  'UserId',
  'Promocode',
  ...Object.values(PARTNER_SIGNUP_KEYS),
] as const;

export interface PartnerSignupSessionSnapshot {
  local: Record<string, string | null>;
  session: Record<string, string | null>;
}

function setPartnerFlag(key: string, value: string): void {
  sessionStorage.setItem(key, value);
  localStorage.setItem(key, value);
}

function getPartnerFlag(key: string): string | null {
  return sessionStorage.getItem(key) || localStorage.getItem(key);
}

function removePartnerFlag(key: string): void {
  sessionStorage.removeItem(key);
  localStorage.removeItem(key);
}

/** Snapshot auth + partner flow state before parent SignupComponent clears storage. */
export function snapshotPartnerSignupSession(): PartnerSignupSessionSnapshot {
  const local: Record<string, string | null> = {};
  for (const key of LOCAL_PRESERVE) {
    local[key] = localStorage.getItem(key);
  }
  const session: Record<string, string | null> = {};
  for (const key of SESSION_PRESERVE) {
    session[key] = sessionStorage.getItem(key);
  }
  return { local, session };
}

/** Restore snapshot after parent constructor clear. */
export function restorePartnerSignupSession(
  snapshot: PartnerSignupSessionSnapshot
): void {
  for (const key of LOCAL_PRESERVE) {
    const value = snapshot.local[key];
    if (value != null && value !== '') {
      localStorage.setItem(key, value);
    }
  }
  for (const key of SESSION_PRESERVE) {
    const value = snapshot.session[key];
    if (value != null && value !== '') {
      sessionStorage.setItem(key, value);
    }
  }
}

export function normalizePartnerReferralSlug(slug: string): string {
  return String(slug || '')
    .split('/')
    .map((part) => part.trim().replace(/\.+$/g, ''))
    .filter(Boolean)
    .join('/');
}

export function normalizePartnerReferralUrl(url: string | null): string | null {
  if (!url) {
    return null;
  }
  const match = url.match(/^(https?:\/\/[^/]+)\/([^/]+)\/referral\/(.+)$/i);
  if (!match) {
    return url;
  }
  const slug = normalizePartnerReferralSlug(match[3]);
  return `${match[1]}/${match[2]}/referral/${slug}`;
}

/** Mirror normal signup Stripe-return flags (session + localStorage). */
export function setSignupStripeReturnFlags(flowPath: string): void {
  sessionStorage.setItem(SIGNUP_STRIPE_RETURN_KEYS.flowPath, flowPath);
  sessionStorage.setItem(SIGNUP_STRIPE_RETURN_KEYS.awaitingPayment, 'true');
  localStorage.setItem(SIGNUP_STRIPE_RETURN_KEYS.flowPath, flowPath);
  localStorage.setItem(SIGNUP_STRIPE_RETURN_KEYS.awaitingPayment, 'true');
}

export function isSignupStripePaymentReturn(): boolean {
  return (
    sessionStorage.getItem(SIGNUP_STRIPE_RETURN_KEYS.awaitingPayment) ===
      'true' ||
    localStorage.getItem(SIGNUP_STRIPE_RETURN_KEYS.awaitingPayment) === 'true'
  );
}

export function clearSignupStripeReturnFlags(): void {
  for (const key of Object.values(SIGNUP_STRIPE_RETURN_KEYS)) {
    sessionStorage.removeItem(key);
    localStorage.removeItem(key);
  }
}

/** Persist partner context immediately before Stripe redirect (session + local). */
export function preparePartnerStripeCheckoutReturn(options: {
  referralCodeValue: string | null;
  promoCode: string | null;
}): void {
  setSignupStripeReturnFlags(PARTNER_SIGNUP_FLOW_PATH);
  setPartnerFlag(PARTNER_SIGNUP_KEYS.flowPath, PARTNER_SIGNUP_FLOW_PATH);
  setPartnerFlag(PARTNER_SIGNUP_KEYS.awaitingPayment, 'true');

  if (options.referralCodeValue) {
    const referral =
      normalizePartnerReferralUrl(options.referralCodeValue) ||
      options.referralCodeValue;
    setPartnerFlag(PARTNER_SIGNUP_KEYS.referralCode, referral);
  }
  if (options.promoCode) {
    setPartnerFlag(PARTNER_SIGNUP_KEYS.referralPromo, options.promoCode);
    setPartnerFlag(PARTNER_SIGNUP_KEYS.promocode, options.promoCode);
  }

  const token = localStorage.getItem('token');
  if (token) {
    setPartnerFlag(PARTNER_SIGNUP_KEYS.authTokenBackup, token);
  }
  const user = sessionStorage.getItem('user') || localStorage.getItem('user');
  if (user) {
    setPartnerFlag(PARTNER_SIGNUP_KEYS.userBackup, user);
    sessionStorage.setItem('user', user);
  }
}

/** Re-apply auth after Stripe or when partner-signup page reloads mid-checkout. */
export function restorePartnerAuthAfterStripe(): void {
  if (!localStorage.getItem('token')) {
    const token = getPartnerFlag(PARTNER_SIGNUP_KEYS.authTokenBackup);
    if (token) {
      localStorage.setItem('token', token);
    }
  }
  if (!sessionStorage.getItem('user')) {
    const user = getPartnerFlag(PARTNER_SIGNUP_KEYS.userBackup);
    if (user) {
      sessionStorage.setItem('user', user);
      localStorage.setItem('user', user);
    }
  }
}

export function isPartnerSignupPaymentReturn(): boolean {
  return (
    getPartnerFlag(PARTNER_SIGNUP_KEYS.awaitingPayment) === 'true' ||
    getPartnerFlag(PARTNER_SIGNUP_KEYS.flowPath) === PARTNER_SIGNUP_FLOW_PATH
  );
}

export function clearPartnerSignupPaymentReturnFlags(): void {
  clearSignupStripeReturnFlags();
  for (const key of PARTNER_STRIPE_RETURN_KEYS) {
    removePartnerFlag(key);
  }
}

export function getPartnerReferralFields(
  referralCodeValue: string | null
): Record<string, string> {
  if (!referralCodeValue) {
    return {};
  }
  const referralCode =
    normalizePartnerReferralUrl(referralCodeValue) || referralCodeValue;
  return { referralCode };
}

/** True when an unauthenticated user should return to /partner-signup (not /signup). */
export function isPartnerSignupReturnRoute(): boolean {
  if (localStorage.getItem('token')?.trim()) {
    return false;
  }
  const referral = getPartnerFlag(PARTNER_SIGNUP_KEYS.referralCode) || '';
  const signupFlowPath =
    localStorage.getItem(SIGNUP_STRIPE_RETURN_KEYS.flowPath) ||
    sessionStorage.getItem(SIGNUP_STRIPE_RETURN_KEYS.flowPath) ||
    '';
  return (
    isPartnerSignupPaymentReturn() ||
    (isSignupStripePaymentReturn() &&
      signupFlowPath === PARTNER_SIGNUP_FLOW_PATH) ||
    referral.includes('/partner-signup/')
  );
}

export function hasStripeSessionIdInUrl(url: string = ''): boolean {
  if (url.includes('session_id=')) {
    return true;
  }
  if (typeof window === 'undefined') {
    return false;
  }
  const search = window.location.search || '';
  const hash = window.location.hash || '';
  return search.includes('session_id=') || hash.includes('session_id=');
}

export function isStripeSubscriptionReturnUrl(url: string): boolean {
  return (
    hasStripeSessionIdInUrl(url) ||
    url.includes('/profile/subscription') ||
    url.includes('/subscription-management')
  );
}

/**
 * Restore partner auth backups and return whether the user should be treated as logged in.
 * Call this before redirecting unauthenticated users to /signup or /partner-signup.
 */
export function restorePartnerAuthIfPresent(): boolean {
  restorePartnerAuthAfterStripe();
  const token = localStorage.getItem('token');
  const user =
    sessionStorage.getItem('user') || localStorage.getItem('user');
  if (user && !sessionStorage.getItem('user')) {
    sessionStorage.setItem('user', user);
  }
  return !!(token?.trim() || user);
}

/** Restore partner auth when landing on subscription page after Stripe checkout. */
export function restorePartnerSessionOnStripeReturn(): void {
  if (
    !isPartnerSignupPaymentReturn() &&
    !localStorage.getItem(PARTNER_SIGNUP_KEYS.authTokenBackup)
  ) {
    return;
  }
  restorePartnerAuthAfterStripe();
}

/** After register + timezone — mirror auth to localStorage before getUserData / Stripe. */
/** Apply auth token returned from sessionUpdate (fresh browser Stripe return). */
export function applyAuthFromStripeSessionResponse(res: any): boolean {
  const data = res?.data || {};
  const token =
    data.authToken || data.token || data.accessToken || res?.authToken;
  if (!token) {
    return false;
  }
  localStorage.setItem('token', token);
  setPartnerFlag(PARTNER_SIGNUP_KEYS.authTokenBackup, token);
  const user = data.user;
  if (user) {
    const userJson =
      typeof user === 'string' ? user : JSON.stringify(user);
    sessionStorage.setItem('user', userJson);
    localStorage.setItem('user', userJson);
    setPartnerFlag(PARTNER_SIGNUP_KEYS.userBackup, userJson);
  }
  return true;
}

export function getPartnerStripeSessionToken(): string | null {
  return (
    localStorage.getItem('token') ||
    getPartnerFlag(PARTNER_SIGNUP_KEYS.authTokenBackup)
  );
}

export function persistPartnerAuthSnapshot(): void {
  const token = localStorage.getItem('token');
  if (token) {
    setPartnerFlag(PARTNER_SIGNUP_KEYS.authTokenBackup, token);
  }
  const user = sessionStorage.getItem('user') || localStorage.getItem('user');
  if (user) {
    setPartnerFlag(PARTNER_SIGNUP_KEYS.userBackup, user);
    sessionStorage.setItem('user', user);
    localStorage.setItem('user', user);
  }
}

export function persistPartnerReferralSession(options: {
  referralCodeValue: string | null;
  promoCode: string | null;
}): void {
  if (options.referralCodeValue) {
    const referral =
      normalizePartnerReferralUrl(options.referralCodeValue) ||
      options.referralCodeValue;
    setPartnerFlag(PARTNER_SIGNUP_KEYS.referralCode, referral);
  } else {
    removePartnerFlag(PARTNER_SIGNUP_KEYS.referralCode);
  }
  if (options.promoCode) {
    setPartnerFlag(PARTNER_SIGNUP_KEYS.referralPromo, options.promoCode);
    setPartnerFlag(PARTNER_SIGNUP_KEYS.promocode, options.promoCode);
  }
}

export function readPartnerReferralFromSession(): {
  referralCodeValue: string | null;
  promoCode: string | null;
} {
  return {
    referralCodeValue: normalizePartnerReferralUrl(
      getPartnerFlag(PARTNER_SIGNUP_KEYS.referralCode)
    ),
    promoCode: getPartnerFlag(PARTNER_SIGNUP_KEYS.referralPromo),
  };
}
